"use client";
import Button from "@marginly/ui/components/button";
import Noop from "@marginly/ui/components/helpers/noop";
import T from "@marginly/ui/components/typography";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";
import { useAccount, useSignMessage } from "wagmi";

import { useMarginlyApi } from "@/hooks/api";
import { useRequest } from "@/hooks/use-request/use-request";

import Space from "../../common/space";
import ConnectWalletModal from "../../navigation/connect-wallet";
import { Header } from "../../point-system/header";
import * as S from "./styled";

const getSignMessage = (RefCode: string) =>
  `I agree to use the following referral link ${RefCode}`;

const ReferralLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <S.Wrapper>
      <Header />
      {children}
      <S.InvitationDecoration src="/images/airdrop/invitation.png" alt="" />
    </S.Wrapper>
  );
};

export function Referral() {
  const { signMessageAsync } = useSignMessage();
  document.documentElement.setAttribute("data-theme", "dark");
  const { address } = useAccount();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [connectModal, setConnectModal] = useState(false);

  const redirect = useCallback(() => {
    router.push("/");
  }, [router]);

  const { data } = useMarginlyApi({
    enabled: Boolean(address),
    endpoint: "userInfo",
    args: [address],
  });

  useEffect(() => {
    if (data?.referralCode) {
      redirect();
    }
  }, [data?.referralCode, redirect]);

  const refCode = searchParams.get("code");

  const [isAcceptClicked, setIsAcceptClicked] = useState(false);

  const { request } = useRequest();

  useEffect(() => {
    if (isAcceptClicked && address) {
      const register = async () => {
        try {
          if (!refCode) return;
          const signature = await signMessageAsync({
            message: getSignMessage(refCode),
          });
          await request("points/referral", {
            method: "POST",
            body: JSON.stringify({ signature, refCode, userAddress: address }),
          });
          redirect();
        } finally {
          setIsAcceptClicked(false);
        }
      };
      register();
    }
  }, [address, isAcceptClicked, redirect, refCode, request, signMessageAsync]);

  const handleClick = async () => {
    setIsAcceptClicked(true);
    if (!address) {
      setConnectModal(true);
    }
  };

  if (!refCode) {
    return (
      <ReferralLayout>
        <S.ReferralTitle>Invalid Referral Code</S.ReferralTitle>
      </ReferralLayout>
    );
  }

  return (
    <ReferralLayout>
      <S.ReferralTitle>Your got an invite</S.ReferralTitle>
      <Space height={6} heightMobile={6} />
      <T body>Sign the message in your wallet and start earning Sparks</T>
      <Space height={58} heightMobile={58} />
      <Button lg className="btn" onClick={handleClick}>
        <T action>Accept Invitation</T>
      </Button>
      {connectModal ? (
        <ConnectWalletModal onHide={() => setConnectModal(false)} />
      ) : (
        <Noop />
      )}
    </ReferralLayout>
  );
}
